import type {
  StorageExternalFacilityType,
  StorageExternalLocationDataStatus,
  StorageLocationsSortBy,
  StorageOfferStatus,
  StorageOffersSortBy,
  StorageProductPeriod,
  StorageCapacityCategory,
  StorageCommodityUnit,
  StorageFlowUnit,
  StorageAllocationMethod,
  StorageProductType,
  StorageSbuUnit,
  StoragePriceTimeUnit,
  StorageTypeOfProduct,
} from 'src/apis/storage/types';
import type { FormOption } from 'src/components/form/form-option';
import { labelsToOptions } from 'src/utils/labels-to-options';
import { labelsToValues } from 'src/utils/labels-to-values';

export const storageLocationsSortByValues = [
  'LOCATION_NAME',
  'LOCATION_BUSINESS_ID',
  'SSO_NAME',
] as const satisfies readonly StorageLocationsSortBy[];

export const storageOffersSortByValues = [
  'OFFER_NAME',
  'SSO_NAME',
  'LOCATION_NAME',
  'OFFER_BUSINESS_ID',
  'TYPE_OF_PRODUCT',
  'PRODUCT_RUNTIME',
  'NEXT_STATUS_SWITCH',
  'OFFER_STATUS',
] as const satisfies readonly StorageOffersSortBy[];

export const productPeriodOrder: StorageProductPeriod[] = [
  'WITHINDAY',
  'DAY',
  'WEEK',
  'MONTH',
  'YEAR',
  'MULTIYEAR',
  'CUSTOM',
];

export const yesNoLabels: Record<'true' | 'false', string> = {
  true: 'Yes',
  false: 'No',
};
export const yesNoValues = labelsToValues(yesNoLabels);
export const yesNoOptions = labelsToOptions(yesNoLabels);

export const storageProductPeriodLabels: Record<StorageProductPeriod, string> =
  {
    WITHINDAY: 'Within Day',
    DAY: 'Day',
    WEEK: 'Week',
    MONTH: 'Month',
    YEAR: 'Year',
    MULTIYEAR: 'Multi-Year',
    CUSTOM: 'Custom',
  };

export const storageProductPeriodCardLabels: Record<
  StorageProductPeriod,
  string
> = {
  WITHINDAY: 'Within Day',
  DAY: 'Daily',
  WEEK: 'Weekly',
  MONTH: 'Monthly',
  YEAR: 'Yearly',
  MULTIYEAR: 'Multi-Year',
  CUSTOM: 'Custom',
};

export const storageProductPeriodOptions = labelsToOptions(
  storageProductPeriodLabels
);
export const storageProductPeriodValues = labelsToValues(
  storageProductPeriodLabels
);

export const storageCapacityCategoryLabels: Record<
  StorageCapacityCategory,
  string
> = {
  FIRM: 'Firm',
  INTERRUPTIBLE: 'Interruptible',
};
export const storageCapacityCategoryOptions = labelsToOptions(
  storageCapacityCategoryLabels
);
export const storageCapacityCategoryValues = labelsToValues(
  storageCapacityCategoryLabels
);

export const storageCommodityUnitLabels: Record<StorageCommodityUnit, string> =
  {
    CUBIC_METER: 'm³',
    KWH: 'kWh',
    MWH: 'MWh',
    GWH: 'GWh',
    TWH: 'TWh',
  };
export const storageCommodityUnitOptions = labelsToOptions(
  storageCommodityUnitLabels
);
export const storageCommodityUnitValues = labelsToValues(
  storageCommodityUnitLabels
);

export const storageFlowUnitLabels: Record<StorageFlowUnit, string> = {
  KWH_PER_HOUR: 'kWh/h',
  KWH_PER_DAY: 'kWh/d',
  MWH_PER_HOUR: 'MWh/h',
  MWH_PER_DAY: 'MWh/d',
  GWH_PER_HOUR: 'GWh/h',
  GWH_PER_DAY: 'GWh/d',
  CUBIC_METER_PER_HOUR: 'm³/h',
};
export const storageFlowUnitOptions = labelsToOptions(storageFlowUnitLabels);
export const storageFlowUnitValues = labelsToValues(storageFlowUnitLabels);

export const storageAllocationMethodLabels: Record<
  StorageAllocationMethod,
  string
> = {
  NO_ALLOCATION: 'No Allocation',
  PAY_AS_BID: 'Pay as Bid',
  UNIFORM_PRICE: 'Uniform Price',
};
export const storageAllocationMethodOptions = labelsToOptions(
  storageAllocationMethodLabels
);
export const storageAllocationMethodValues = labelsToValues(
  storageAllocationMethodLabels
);

export const storageProductTypeValues = [
  'storageVolume',
  'injectionCapacity',
  'withdrawalCapacity',
  'SBU',
] as const satisfies readonly StorageProductType[];

export const productTypeOptions: FormOption<StorageProductType>[] = [
  { label: 'Storage Volume', value: 'storageVolume' },
  { label: 'Injection Capacity', value: 'injectionCapacity' },
  { label: 'Withdrawal Capacity', value: 'withdrawalCapacity' },
  { label: 'SBU', value: 'SBU' },
];

export const storagePriceTimeUnitLabels: Record<StoragePriceTimeUnit, string> =
  {
    RUNTIME: 'Runtime',
    HOUR: 'h',
    DAY: 'd',
    WEEK: 'w',
    MONTH: 'm',
    YEAR: 'y',
  };

export const storagePriceTimeUnitOptionLabels: Record<
  StoragePriceTimeUnit,
  string
> = {
  RUNTIME: 'Runtime',
  HOUR: 'Hour',
  DAY: 'Day',
  WEEK: 'Week',
  MONTH: 'Month',
  YEAR: 'Year',
};

export const storagePriceTimeUnitOptions = labelsToOptions(
  storagePriceTimeUnitOptionLabels
);
export const storagePriceTimeUnitValues = labelsToValues(
  storagePriceTimeUnitLabels
);

export const SBU_UNIT: StorageSbuUnit = 'SBU';

export const sbuUnitOption: FormOption<StorageSbuUnit>[] = [
  { label: 'SBU', value: SBU_UNIT },
];

export const initialValuesByProductType: {
  storageVolume: { typeOfProductUnit: StorageCommodityUnit };
  injectionCapacity: { typeOfProductUnit: StorageFlowUnit };
  withdrawalCapacity: { typeOfProductUnit: StorageFlowUnit };
  SBU: { typeOfProductUnit: StorageSbuUnit };
} = {
  storageVolume: { typeOfProductUnit: 'CUBIC_METER' },
  injectionCapacity: { typeOfProductUnit: 'KWH_PER_HOUR' },
  withdrawalCapacity: { typeOfProductUnit: 'KWH_PER_HOUR' },
  SBU: { typeOfProductUnit: SBU_UNIT },
};

export const publicStorageOfferStatusLabels: Record<
  Exclude<StorageOfferStatus, 'CREATED'>,
  string
> = {
  DELETED: 'Deleted',
  PUBLISHED: 'Published',
  OPEN: 'Open',
  WAITING_FOR_NEXT_ROUND: 'Waiting for Next Round',
  APPROVAL_PENDING: 'Approval Pending',
  FINISHED: 'Finished',
  UNSUCCESSFUL: 'Unsuccessful',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
};

export const storageOfferStatusLabels: Record<StorageOfferStatus, string> = {
  ...publicStorageOfferStatusLabels,
  CREATED: 'Created',
};

export const storageOffersProductTypeLabels: Record<
  StorageTypeOfProduct['typeOfProduct'],
  string
> = {
  STORAGE_VOLUME: 'Storage Volume',
  INJECTION_CAPACITY: 'Injection Capacity',
  WITHDRAWAL_CAPACITY: 'Withdrawal Capacity',
  SBU: 'SBU',
};

export const storageOffersProductTypeValues = [
  'STORAGE_VOLUME',
  'INJECTION_CAPACITY',
  'WITHDRAWAL_CAPACITY',
  'SBU',
] as const satisfies readonly StorageTypeOfProduct['typeOfProduct'][];

export const storageTypeOfProductFilterOptions: {
  label: string;
  value: StorageTypeOfProduct['typeOfProduct'];
}[] = storageOffersProductTypeValues.map((value) => ({
  label: storageOffersProductTypeLabels[value],
  value,
}));

export const storageOfferStatusValues = [
  'CREATED',
  'DELETED',
  'PUBLISHED',
  'OPEN',
  'WAITING_FOR_NEXT_ROUND',
  'APPROVAL_PENDING',
  'FINISHED',
  'UNSUCCESSFUL',
  'CANCELLED',
  'FAILED',
] as const satisfies readonly StorageOfferStatus[];

export const storagePublicActiveOfferStatuses = [
  'OPEN',
  'PUBLISHED',
  'APPROVAL_PENDING',
] as const satisfies readonly StorageOfferStatus[];

export const storageActiveOfferStatuses = [
  ...storagePublicActiveOfferStatuses,
  'CREATED',
] as const satisfies readonly StorageOfferStatus[];

export const storageFinishedOfferStatuses = [
  'FINISHED',
  'UNSUCCESSFUL',
] as const satisfies readonly StorageOfferStatus[];

export function getStorageActiveOfferStatuses(
  includeCreated: boolean
): StorageOfferStatus[] {
  return includeCreated
    ? [...storageActiveOfferStatuses]
    : [...storagePublicActiveOfferStatuses];
}

export const storageExternalFacilityTypeLabels: Record<
  StorageExternalFacilityType,
  string
> = {
  DSR: 'Depleted Field',
  ASR: 'Aquifer',
  ASF: 'Salt Cavern',
  SRC: 'Rock Cavern',
  GRP: 'Virtual Storage Group',
  UNKNOWN: 'Unknown',
};

export const storageExternalLocationDataStatusLabels: Record<
  StorageExternalLocationDataStatus,
  string
> = {
  ESTIMATED: 'Estimated',
  CONFIRMED: 'Confirmed',
  NO_DATA: 'No Data',
};
